import { Link, NavLink, Outlet } from 'react-router-dom';
import { Headphones, History, LayoutDashboard, LogOut, Shield, Users } from 'lucide-react';
import { useSupportAuth } from '@/context/SupportAuthContext';
import { cn } from '@/lib/utils';
import { BRAND_NAME, LOGO } from '@/lib/brand';

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex flex-col md:flex-row items-center justify-center md:justify-start gap-0.5 md:gap-2 px-2 md:px-3 py-2 md:py-2.5 rounded-lg text-[10px] md:text-sm font-medium transition-colors min-w-[4.5rem] md:min-w-0',
    isActive
      ? 'text-foreground bg-muted md:bg-foreground md:text-background'
      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
  );

export function SupportShell() {
  const { agent, admin, signOut } = useSupportAuth();

  return (
    <div className="h-[100dvh] bg-background text-foreground flex flex-col md:flex-row overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-56 lg:w-60 border-r border-border p-4 shrink-0 flex-col">
        <Link to="/support/dashboard" className="flex items-center gap-2.5 mb-6">
          <img src={LOGO.withoutBg} alt="" className="h-8 w-8 object-contain dark:hidden" />
          <img src={LOGO.dark} alt="" className="h-8 w-8 object-contain hidden dark:block rounded-full" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{BRAND_NAME}</div>
            <div className="text-[11px] text-muted-foreground">Support Console</div>
          </div>
        </Link>
        <nav className="flex flex-col gap-1 flex-1">
          <NavLink to="/support/dashboard" className={navItemClass} end={false}>
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </NavLink>
          <NavLink to="/support/history" className={navItemClass}>
            <History className="h-4 w-4" /> History
          </NavLink>
          {admin && (
            <NavLink to="/support/admin" className={navItemClass}>
              <Users className="h-4 w-4" /> Agents
            </NavLink>
          )}
        </nav>
        <div className="pt-4 border-t border-border space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {admin ? <Shield className="h-3.5 w-3.5 shrink-0" /> : <Headphones className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">{agent?.name || 'Support user'}</span>
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header
        className="md:hidden shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background/95 backdrop-blur"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <Link to="/support/dashboard" className="flex items-center gap-2 min-w-0">
          <img src={LOGO.withoutBg} alt="" className="h-7 w-7 object-contain dark:hidden" />
          <img src={LOGO.dark} alt="" className="h-7 w-7 object-contain hidden dark:block rounded-full" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">Support</div>
            <div className="text-[10px] text-muted-foreground truncate">{agent?.name || 'Console'}</div>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => void signOut()}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <main className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden shrink-0 grid border-t border-border bg-background/95 backdrop-blur"
        style={{
          gridTemplateColumns: admin ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
          paddingBottom: 'max(0.35rem, env(safe-area-inset-bottom))',
        }}
      >
        <NavLink to="/support/dashboard" className={navItemClass}>
          <LayoutDashboard className="h-5 w-5" />
          Inbox
        </NavLink>
        <NavLink to="/support/history" className={navItemClass}>
          <History className="h-5 w-5" />
          History
        </NavLink>
        {admin && (
          <NavLink to="/support/admin" className={navItemClass}>
            <Users className="h-5 w-5" />
            Agents
          </NavLink>
        )}
      </nav>
    </div>
  );
}
